import React from 'react';
import { Chip } from '@/components/shared/Chip';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useServices } from '@/providers/ServicesProvider';
import styles from './SetlistChipRow.module.scss';

export interface SetlistChipRowProps {
	serviceId?: string | null;
	bibleReference?: string;
	totalSongPositions: number;
	postSermonSongPosition?: number | null;
	onServiceClick?: () => void;
	onSongsClick?: () => void;
	onPassageClick?: () => void;
	hideServiceChip?: boolean;
	disabled?: boolean;
}

export const SetlistChipRow: React.FC<SetlistChipRowProps> = ({
	serviceId,
	bibleReference,
	totalSongPositions,
	postSermonSongPosition,
	onServiceClick,
	onSongsClick,
	onPassageClick,
	hideServiceChip,
	disabled,
}) => {
	const { services } = useServices();

	const selectedService = services.find((s) => s.id === serviceId);

	// Songs chip value: "pre, post" e.g. "3, 1"
	const preCount =
		typeof postSermonSongPosition === 'number'
			? postSermonSongPosition - 1
			: totalSongPositions;
	const postCount =
		typeof postSermonSongPosition === 'number'
			? totalSongPositions - (postSermonSongPosition - 1)
			: 0;
	const songsValue =
		typeof postSermonSongPosition === 'number'
			? `${preCount}, ${postCount}`
			: `${totalSongPositions}`;

	const serviceLabel = selectedService ? selectedService.name : undefined;
	const passageLabel = bibleReference || undefined;

	const fireOpenService = useAnalyticsEvent('Generate_Setlist_Open_Service_Chip');
	const fireOpenSongs = useAnalyticsEvent('Generate_Setlist_Open_Songs_Chip');
	const fireOpenPassage = useAnalyticsEvent('Generate_Setlist_Open_Passage_Chip');

	const handleServiceClick = () => {
		fireOpenService();
		onServiceClick?.();
	};

	const handleSongsClick = () => {
		fireOpenSongs();
		onSongsClick?.();
	};

	const handlePassageClick = () => {
		fireOpenPassage();
		onPassageClick?.();
	};

	return (
		<div className={styles['chip-row']}>
			{!hideServiceChip && (
				<Chip
					variant="rounded"
					className={styles['chip--filled']}
					disabled={disabled}
					onClick={disabled ? undefined : handleServiceClick}
				>
					{serviceLabel ? `Service: ${serviceLabel}` : 'Service: None'}
				</Chip>
			)}
			<Chip
				variant="rounded"
				className={styles['chip--filled']}
				disabled={disabled}
				onClick={disabled ? undefined : handleSongsClick}
			>
				{`Songs: ${songsValue}`}
			</Chip>
			<Chip
				variant="rounded"
				className={passageLabel ? styles['chip--filled'] : styles['chip--outlined']}
				disabled={disabled}
				onClick={disabled ? undefined : handlePassageClick}
			>
				{passageLabel ? `Passage: ${passageLabel}` : 'Passage: None'}
			</Chip>
		</div>
	);
};
